import { useEffect, useState } from "react";
import { api, apiError, eur, deDate } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { FileBarChart, Download, Package, FileSpreadsheet, FileText, Users, Archive } from "lucide-react";

const YEARS = [2024, 2025, 2026];

export default function Steuerberater() {
  const now = new Date();
  const [von, setVon] = useState(`${now.getFullYear()}-01-01`);
  const [bis, setBis] = useState(`${now.getFullYear()}-12-31`);
  const [ov, setOv] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!von || !bis) return;
    api.get(`/finance/overview?von=${von}&bis=${bis}`).then((r) => setOv(r.data))
      .catch((e) => { setOv(null); toast.error(apiError(e, "Zusammenfassung konnte nicht geladen werden")); });
  }, [von, bis]);

  const download = async (path, filename, key) => {
    setBusy(key);
    try {
      const { data: blob } = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([blob]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${filename} wurde heruntergeladen.`);
    } catch (e) { toast.error(apiError(e, "Export fehlgeschlagen")); }
    setBusy("");
  };

  const contents = [
    { icon: FileBarChart, label: "Gewinn- und Verlustübersicht (PDF)" },
    { icon: FileSpreadsheet, label: "Excel-Arbeitsmappe: Verkäufe, Einkäufe, Rechnungen, Gutschriften, Kunden, Lieferanten, Lager" },
    { icon: FileText, label: "Alle Rechnungen des Zeitraums (PDF)" },
    { icon: Archive, label: "Ankaufbelege & Gutschriften (PDF)" },
    { icon: Users, label: "Kunden- und Lieferantenverzeichnis" },
  ];

  return (
    <div className="max-w-4xl space-y-6" data-testid="steuerberater-page">
      <div>
        <div className="text-xs font-mono uppercase tracking-wider text-zinc-400">Finanzen</div>
        <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-1">Steuerberater-Export</h1>
        <p className="text-sm text-zinc-500 mt-1">Zeitraum wählen – L19 stellt automatisch alle Unterlagen für den Steuerberater zusammen. Keine doppelte Dateneingabe.</p>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl p-5 shadow-sm space-y-4" data-testid="tax-period-card">
        <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">Zeitraum</div>
        <div className="flex gap-2 flex-wrap">
          {YEARS.map((y) => (
            <button key={y} onClick={() => { setVon(`${y}-01-01`); setBis(`${y}-12-31`); }} data-testid={`tax-year-${y}`}
              className={`text-xs font-mono px-3 py-1.5 rounded-full border min-h-[36px] ${von === `${y}-01-01` ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-200 text-zinc-500 hover:border-zinc-400"}`}>{y}</button>
          ))}
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <input type="date" value={von} onChange={(e) => setVon(e.target.value)} data-testid="tax-von" className="border border-zinc-200 rounded-lg px-3 py-2 text-sm bg-white min-h-[44px]" />
          <span className="text-zinc-400 text-xs">bis</span>
          <input type="date" value={bis} onChange={(e) => setBis(e.target.value)} data-testid="tax-bis" className="border border-zinc-200 rounded-lg px-3 py-2 text-sm bg-white min-h-[44px]" />
        </div>
      </div>

      {ov && (
        <div className="bg-white border border-zinc-200 rounded-xl p-5 shadow-sm" data-testid="tax-summary">
          <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 mb-3">Zusammenfassung {deDate(von)} – {deDate(bis)}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 text-sm">
            <div className="flex justify-between"><span className="text-zinc-500">Einnahmen (Umsatz)</span><span className="font-mono font-medium">{eur(ov.umsatz)}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Ausgaben</span><span className="font-mono font-medium">{eur(ov.kosten_gesamt)}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Gewinn</span><span className="font-mono font-medium text-emerald-600">{eur(ov.gewinn)}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Umsatzsteuer</span><span className="font-mono">{eur(ov.umsatzsteuer)}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Vorsteuer</span><span className="font-mono">{eur(ov.vorsteuer)}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Lagerwert</span><span className="font-mono">{eur(ov.lagerwert)}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Verkäufe / Einkäufe</span><span className="font-mono">{ov.anzahl.verkaeufe} / {ov.anzahl.einkaeufe}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Rechnungen</span><span className="font-mono">{ov.anzahl.rechnungen}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Gutschriften</span><span className="font-mono">{ov.anzahl.gutschriften}</span></div>
          </div>
        </div>
      )}

      <div className="bg-white border border-zinc-200 rounded-xl p-5 shadow-sm">
        <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 mb-3">Der Export enthält</div>
        <div className="space-y-2.5">
          {contents.map((c, i) => (
            <div key={i} className="flex items-center gap-3 text-sm text-zinc-600">
              <c.icon className="w-4 h-4 text-zinc-400 shrink-0" /> {c.label}
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        <Button onClick={() => download(`/finance/tax-export?von=${von}&bis=${bis}`, `Steuerberater-Export-${von}_${bis}.zip`, "tax")}
          disabled={!!busy} data-testid="tax-export-button"
          className="bg-zinc-900 hover:bg-zinc-800 text-white min-h-[48px] flex-1 sm:flex-none">
          <Package className="w-4 h-4 mr-1" /> {busy === "tax" ? "Erstelle Export…" : "Steuerberater-Export erstellen (ZIP)"}
        </Button>
        <Button variant="outline" onClick={() => download("/finance/full-export", "L19-Datenexport.zip", "full")}
          disabled={!!busy} data-testid="full-export-button" className="min-h-[48px]">
          <Download className="w-4 h-4 mr-1" /> {busy === "full" ? "Erstelle…" : "Alle Daten herunterladen (CSV)"}
        </Button>
        <Button variant="outline" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/finance/guv-pdf?von=${von}&bis=${bis}`, "_blank")}
          data-testid="tax-guv-pdf" className="min-h-[48px]">
          <FileBarChart className="w-4 h-4 mr-1" /> GuV-PDF
        </Button>
      </div>

      <div className="bg-zinc-100 border border-zinc-200 rounded-xl p-4 text-xs text-zinc-500" data-testid="tax-notice">
        Alle Werte werden automatisch aus den vorhandenen Ankäufen, Verkäufen, Rechnungen und Kosten berechnet.
        Der Export ist für die Weitergabe an den Steuerberater geeignet (ZIP mit PDF, Excel und Belegen).
      </div>
    </div>
  );
}
